import React from "react"
import { Box, BoxProps, SimpleGrid } from "@chakra-ui/core"
import { useQueryParam, NumberParam } from "use-query-params"
import truncate from "lodash/truncate"

import {
  ContentfulBlogPost,
  ContentfulAsset,
  Maybe,
} from "../../../graphql-types"
import Body from "../typography/Body"
import ArticleBlock from "../ArticleBlock"
import { rawRichTextToString } from "../../utils/richText"
import { FluidObject } from "gatsby-image"
import Pagination from "../Pagination"
import { placeholderImage } from "../../utils"
import ArticleBlockItem from "../ArticleBlockItem"
import { bp } from "../../utils/MediaQueries"

type ArticleListProps = BoxProps & {
  articles: (Pick<ContentfulBlogPost, "excerpt" | "slug" | "title"> & {
    postImage?: Maybe<Pick<ContentfulAsset, "fluid">>
  })[]
  pageSize?: number
  emptyText?: string
}

export default function ArticleList({
  articles: allArticles,
  pageSize = 9,
  emptyText = "No articles available",
  ...props
}: ArticleListProps) {
  const [page = 1, setPage] = useQueryParam("page", NumberParam)
  const baseZeroPage = page! - 1

  const [latestArticle, ...articles] = allArticles
  const pageCount = Math.ceil(articles.length / pageSize)

  const handleNext = () => {
    setPage(Math.min(page! + 1, pageCount))
  }
  const handlePrev = () => {
    setPage(Math.max(page! - 1, 1))
  }

  return (
    <Box {...props}>
      {!latestArticle && (
        <Body size="lg" color="dawn" mt="3rem" textAlign="center">
          {emptyText}
        </Body>
      )}
      {latestArticle && (
        <ArticleBlock
          tag="Latest"
          title={latestArticle.title!}
          subtext={truncate(rawRichTextToString(latestArticle?.excerpt?.raw), {
            length: 86,
          })}
          to={`/blogs/bike-advice/${latestArticle.slug}`}
          image={
            (latestArticle.postImage?.fluid || placeholderImage) as FluidObject
          }
          mb="7.5rem"
        />
      )}
      {articles.length > 0 && (
        <>
          <SimpleGrid
            columns={[1, null, null, null, 2, 3]}
            spacingX="1.25rem"
            spacingY="3.625rem"
            mb={bp("3rem", "7.5rem")}
          >
            {articles
              .slice(
                baseZeroPage * pageSize,
                baseZeroPage * pageSize + pageSize
              )
              .map((article) => (
                <ArticleBlockItem
                  key={article.slug}
                  title={article.title!}
                  subtext={truncate(rawRichTextToString(article.excerpt?.raw), {
                    length: 79,
                  })}
                  to={`/blogs/bike-advice/${article.slug}`}
                  image={
                    (article.postImage?.fluid ||
                      placeholderImage) as FluidObject
                  }
                />
              ))}
          </SimpleGrid>
          <Pagination
            width="45.125rem"
            maxWidth="100%"
            mx="auto"
            page={page || 1}
            pageCount={pageCount}
            onNext={handleNext}
            onPrevious={handlePrev}
            mb="2.5rem"
          />
        </>
      )}
    </Box>
  )
}
